import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

import Icon from '~/components/Icon/Icon';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * Info modal rendered in a portal: a bottom sheet on mobile and a centered
 * dialog from `min-[1025px]`. Closes on Escape, backdrop click or the primary
 * "Отлично" button. Used by the DubaiExpress product notice badges to show
 * warranty/import terms.
 */
const ProductInfoModal = ({ isOpen, onClose, title, children }: Props) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    document.body.classList.add('body-no-scroll');

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.classList.remove('body-no-scroll');
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-end justify-center min-[1025px]:items-center min-[1025px]:p-4">
      <button
        type="button"
        aria-label="Закрыть"
        className="absolute inset-0 cursor-default border-0 bg-black/40"
        onClick={onClose}
      />
      <div className="relative flex max-h-[90vh] w-full flex-col gap-3 overflow-y-auto rounded-t-[16px] bg-bg-content px-4 pt-4 pb-[max(2rem,env(safe-area-inset-bottom))] shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)] min-[1025px]:max-w-[500px] min-[1025px]:rounded-[16px] min-[1025px]:px-8 min-[1025px]:pb-8">
        <div className="flex items-center gap-2 py-2">
          <h2 className="m-0 flex-1 font-customer-heading font-medium text-[24px] text-text-primary leading-7 tracking-tightest">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Закрыть"
            onClick={onClose}
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-bg-content text-icon-primary shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]"
          >
            <Icon type="close" size={24} />
          </button>
        </div>
        <div className="flex flex-col gap-3 font-customer-text font-normal text-[14px] text-text-primary leading-6 tracking-tight">
          {children}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 flex h-14 w-full shrink-0 cursor-pointer items-center justify-center rounded-[12px] border-0 bg-graphic-regular-brand font-customer-text font-semibold text-[18px] text-white shadow-[0px_4px_20px_rgba(0,0,0,0.12)]"
        >
          Отлично
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default ProductInfoModal;
